import React, { useEffect, useRef, useState } from "react";
import { message, Button, Empty, Input, Modal, PageHeader, Result, Space, Table, Form } from "antd";
import { ColumnsType } from "antd/es/table";
import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined, DeleteFilled, EditOutlined, FileExcelOutlined } from "@ant-design/icons";
import { FilterConfirmProps, FilterDropdownProps } from "antd/lib/table/interface";
import Highlighter from "react-highlight-words";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const { confirm } = Modal;

const Portfolios: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [data, setData] = useState<Array<any> | null>(null);
    const [search, setSearch] = useState<{ text: string; column: React.ReactNode | null }>({ text: "", column: null });
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPortfolio, setEditingPortfolio] = useState<any | null>(null);
    const [form] = Form.useForm();
    const searchInput = useRef<any>(null);

    const confirmDelete = (portfolio: any) => {
        confirm({
            title: `Delete ${portfolio.name}?`,
            icon: <ExclamationCircleOutlined />,
            content: "This action cannot be undone!",
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            onOk: async () => {
                try {
                    const response = await fetch(`/api/Portfolios/${portfolio.id}`, {
                        method: "DELETE",
                        headers: { Accept: "application/json" }
                    });

                    if (response.ok) {
                        message.success("Portfolio deleted");
                        await fetchData();
                    }
                } catch (error) {
                    message.error("Error deleting portfolio");
                }
            }
        });
    };

    const handleExport = () => {
        if (!data) return;

        const exportData = data.map((portfolio) => ({
            Name: portfolio.name,
            "Capacity (KW)": portfolio.capacity,
            NumberOfAssets: portfolio.numberOfAssets
        }));

        const headers = Object.keys(exportData[0]) as (keyof (typeof exportData)[0])[];

        const columnWidths = headers.map((key) => ({
            wch: Math.max(key.length, ...exportData.map((row) => (row[key] ? row[key]!.toString().length : 0))) + 5
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        worksheet["!cols"] = columnWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Portfolios");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "portfolios.xlsx");
    };

    const showModal = (portfolio: any | null) => {
        setEditingPortfolio(portfolio);
        form.setFieldsValue(portfolio || { name: "" });
        setIsModalVisible(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingPortfolio) {
                const response = await fetch(`/api/Portfolios/${editingPortfolio.id}`, {
                    method: "PUT",
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    body: JSON.stringify(values)
                });

                if (response.ok) {
                    message.success("Portfolio updated");
                    await fetchData();
                }
            } else {
                const response = await fetch(`/api/Portfolios`, {
                    method: "POST",
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    body: JSON.stringify(values)
                });

                if (response.ok) {
                    message.success("Portfolio created");
                    await fetchData();
                }
            }
            form.resetFields();
            setIsModalVisible(false);
        } catch (error) {
            message.error("Error saving portfolio");
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setIsModalVisible(false);
    };

    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: (props: FilterDropdownProps) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={props.selectedKeys[0]}
                    onChange={(e) => props.setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(props.selectedKeys, props.confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(props.selectedKeys, props.confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(props.clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value: string | number | boolean, record: any) => record[dataIndex].toString().toLowerCase().includes(value.toString().toLowerCase()),
        onFilterDropdownVisibleChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string) => (
            <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[search.text]} autoEscape textToHighlight={text || ""} />
        )
    });

    const handleSearch = (selectedKeys: React.Key[], confirm: (param?: FilterConfirmProps) => void, dataIndex: string) => {
        confirm();
        setSearch({ text: selectedKeys[0] as string, column: dataIndex });
    };

    const handleReset = (clearFilters?: () => void) => {
        clearFilters?.();
        setSearch({ text: "", column: null });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/Portfolios", {
                headers: { Accept: "application/json" }
            });

            if (!response.ok) throw new Error();
            const portfolios = await response.json();
            setData(portfolios);
            setHasError(false);
        } catch (error) {
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnsType<any> = [
        {
            title: "Name",
            dataIndex: "name",
            ...getColumnSearchProps("name"),
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: "Capacity",
            dataIndex: "capacity",
            render: (capacity) => <div>{Math.round(10 * (capacity / 1000)) / 10}MW</div>,
            sorter: (a, b) => (a.capacity || 0) - (b.capacity || 0)
        },
        {
            title: "Number of Assets",
            dataIndex: "numberOfAssets",
            sorter: (a, b) => (a.numberOfAssets || 0) - (b.numberOfAssets || 0)
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (text, record) => {
                return (
                    <>
                        <Button icon={<EditOutlined />} type="primary" onClick={() => showModal(record)}></Button>
                        <Button danger type="primary" icon={<DeleteFilled />} onClick={() => confirmDelete(record)}></Button>
                    </>
                );
            }
        }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <PageHeader
                title="Portfolios"
                extra={[
                    <div key="park-buttons">
                        <Button.Group>
                            <Button key="add" type="primary" onClick={() => showModal(null)} icon={<PlusOutlined />}>
                                Add Portfolio
                            </Button>
                            <Button
                                key="export"
                                type="default"
                                style={{ backgroundColor: "green", color: "white", borderColor: "green" }}
                                onClick={handleExport}
                                icon={<FileExcelOutlined />}
                            >
                                Export to Excel
                            </Button>
                        </Button.Group>
                    </div>
                ]}
            />

            {hasError ? (
                <Result status="error" title="Failed to load portfolios" extra={<Button onClick={fetchData}>Retry</Button>} />
            ) : (
                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    pagination={{
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showSizeChanger: true,
                        defaultPageSize: 10
                    }}
                    dataSource={data || []}
                    locale={{
                        emptyText: data ? <Empty description="No portfolios found" /> : null
                    }}
                />
            )}

            <Modal title={editingPortfolio ? "Edit Portfolio" : "Add Portfolio"} visible={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter the portfolio name!" }]}>
                        <Input placeholder="Enter the portfolio name" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default Portfolios;
